import React, { useState } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect } from 'firebase/auth';
import { auth } from '../../backend/firebase';
import { useAppSelector } from '../../redux/hooks';
import { selectUid, selectShowUpgradePrompt, selectAssignedColorRgb } from '../../redux/features/auth/slice';
import { colorToHex } from '../../redux/features/color/color';
import { persistor } from '../../redux/store';
import { track } from '../../helpers/analytics';
import { signInWithApple } from '../../helpers/appleSignIn';
import { t } from '../../i18n';
import ProfileModal from './ProfileModal';
import './SignInButton.scss';

const provider = new GoogleAuthProvider();

const SignInButton = () => {
    const uid = useAppSelector(selectUid);
    const showUpgradePrompt = useAppSelector(selectShowUpgradePrompt);
    const assignedColorRgb = useAppSelector(selectAssignedColorRgb);
    const [showProfile, setShowProfile] = useState(false);
    const [showProviders, setShowProviders] = useState(false);

    if (showUpgradePrompt) return null;

    const handleGoogleSignIn = async () => {
        track('sign_in_started', { trigger: 'manual', provider: 'google' });
        try {
            await signInWithPopup(auth, provider);
        } catch (err: any) {
            if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
                await persistor.flush();
                await signInWithRedirect(auth, provider);
            } else {
                console.error('Sign-in error:', err);
            }
        }
    };

    const handleAppleSignIn = async () => {
        track('sign_in_started', { trigger: 'manual', provider: 'apple' });
        await signInWithApple();
    };

    if (uid !== null && auth.currentUser !== null) {
        const { photoURL, email, displayName } = auth.currentUser;
        return (
            <>
                <button
                    className="profile-btn"
                    onClick={() => setShowProfile(true)}
                    aria-label={t('web.auth.profile')}
                    style={assignedColorRgb ? { backgroundColor: colorToHex(assignedColorRgb) } : undefined}
                >
                    {photoURL
                        ? <img src={photoURL} alt={displayName ?? t('web.auth.profile')} referrerPolicy="no-referrer" />
                        : <span>{(displayName ?? email ?? '?')[0].toUpperCase()}</span>
                    }
                </button>
                {showProfile && (
                    <ProfileModal
                        email={email ?? null}
                        photoURL={photoURL ?? null}
                        displayName={displayName ?? null}
                        onClose={() => setShowProfile(false)}
                    />
                )}
            </>
        );
    }

    // First click expands a small provider chooser (Google / Apple)
    if (showProviders) {
        return (
            <div className="sign-in-providers">
                <button className="sign-in-btn sign-in-btn--static" onClick={handleGoogleSignIn}>
                    {t('web.auth.signInWithGoogle')}
                </button>
                <button className="sign-in-btn sign-in-btn--static sign-in-btn--apple" onClick={handleAppleSignIn}>
                    {t('web.auth.signInWithApple')}
                </button>
            </div>
        );
    }

    return (
        <button className="sign-in-btn" onClick={() => setShowProviders(true)}>
            {t('web.auth.signIn')}
        </button>
    );
};

export default SignInButton;
